/**
 * CemeteryDetail — one cemetery + a list of memorials linked to it.
 * Route: `/cemeteries/:slug`.
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { http } from "@/lib/api";
import SeoHead from "@/components/SeoHead";
import { ArrowLeft, MapPin, Users } from "lucide-react";
import MemorialBadges from "@/components/MemorialBadges";

export default function CemeteryDetail() {
  const { slug } = useParams();
  const [cem, setCem] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    http.get(`/cemeteries/${encodeURIComponent(slug)}`)
      .then(({ data }) => alive && setCem(data))
      .catch((e) => alive && setError(e?.response?.data?.detail || "Not found"))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [slug]);

  if (loading) return <div className="max-w-4xl mx-auto py-12 px-6 text-sm opacity-70">Loading…</div>;
  if (error || !cem) return (
    <div className="max-w-4xl mx-auto py-12 px-6">
      <Link to="/cemeteries" className="text-sm underline" data-testid="cemetery-back">
        <ArrowLeft className="inline w-3 h-3" /> Back to directory
      </Link>
      <div className="mt-4 text-sm opacity-70">{error || "Not found"}</div>
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto py-8 px-4 lg:px-8" data-testid="cemetery-detail">
      <SeoHead
        title={`${cem.name} · Cemetery Directory`}
        description={cem.summary || `${cem.name} — ${cem.city}, ${cem.country}`}
      />
      <Link to="/cemeteries" className="text-xs opacity-70 hover:opacity-100" data-testid="cemetery-back">
        <ArrowLeft className="inline w-3 h-3" /> Back to directory
      </Link>
      <div className="overline mt-3" style={{ color: "var(--wofy-primary)" }}>CEMETERY · {cem.religion?.toUpperCase()}</div>
      <h1 className="font-display text-4xl md:text-5xl mt-1 leading-tight">{cem.name}</h1>
      {cem.aka && <div className="mt-1 italic" style={{ color: "var(--wofy-text-soft)" }}>{cem.aka}</div>}
      <div className="mt-3 text-sm flex flex-wrap items-center gap-3" style={{ color: "var(--wofy-text-soft)" }}>
        <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {[cem.city, cem.state, cem.country].filter(Boolean).join(" · ")}</span>
        {cem.established && <span>· Established {cem.established > 0 ? cem.established : `${Math.abs(cem.established)} BCE`}</span>}
        {cem.burial_count && <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" /> {cem.burial_count} burials</span>}
      </div>

      {cem.summary && (
        <p className="mt-6 text-lg leading-relaxed">{cem.summary}</p>
      )}

      <div className="mt-8 text-xs" style={{ color: "var(--wofy-text-soft)" }}>
        Traditions and burial customs vary — read our editorial guide:&nbsp;
        <Link
          to="/articles/indian-funeral-traditions-and-memorial-practices"
          className="underline"
          style={{ color: "var(--wofy-primary)" }}
        >
          Indian Funeral Traditions &amp; Memorial Practices →
        </Link>
      </div>

      {/* Linked memorials */}
      <div className="mt-8" data-testid="cemetery-memorials">
        <h2 className="font-display text-2xl">Memorials at this site</h2>
        {(cem.linked_memorials || []).length === 0 ? (
          <div className="mt-2 text-sm opacity-70">
            No memorials linked yet. Families — when submitting a memorial, include the cemetery name so we can list your loved one here.
            {" "}<Link to="/obituaries/submit" className="underline">Submit a memorial →</Link>
          </div>
        ) : (
          <div className="mt-3 grid sm:grid-cols-2 gap-3">
            {cem.linked_memorials.map((m) => (
              <Link
                key={m.id}
                to={`/celebrate/${m.slug || m.id}`}
                className="wofy-card p-3 flex items-center gap-3 hover:shadow-md transition"
                style={{ borderColor: "var(--wofy-border)" }}
              >
                {m.primary_portrait ? (
                  <img src={m.primary_portrait} alt="" className="w-12 h-12 rounded-full object-cover shrink-0" />
                ) : (
                  <div className="w-12 h-12 rounded-full shrink-0" style={{ background: "var(--wofy-surface)" }} />
                )}
                <div className="min-w-0 flex-1">
                  <div className="font-display text-sm truncate">{m.full_name}</div>
                  <div className="text-[10px] font-mono opacity-70 truncate">
                    {[m.birth_date?.slice(0, 4), m.death_date?.slice(0, 4)].filter(Boolean).join(" — ")}
                  </div>
                  {(m.verification_badges?.length > 0) && (
                    <MemorialBadges badges={m.verification_badges} size="sm" className="mt-1" testidPrefix={`cem-memorial-badge-${m.id}`} />
                  )}
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
